/* eslint-disable react/jsx-no-bind */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarTodayOutlined,
  CheckCircleOutline,
  Close,
  LocationOnOutlined,
  PersonOutlineOutlined,
  PhotoCameraOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import { MEDIA_TYPES } from '../Task.model';
import colors from '../../colors.styles';
import adminFormStyles from '../../shared/styles/adminForm.styles';

export const TASK_REVIEW_MODES = {
  LAUNCH: 'launch',
  UPDATE: 'update',
};

const COPY = {
  [TASK_REVIEW_MODES.LAUNCH]: {
    title: 'Ready to go live?',
    subtitle: 'Review your request before posting it to stores.',
    confirmLabel: 'Set request live',
    confirmLoadingLabel: 'Setting live...',
    // Fresh reach, calculated when the modal opens.
    targetingLabel: 'Targeting',
  },
  [TASK_REVIEW_MODES.UPDATE]: {
    title: 'Update your request?',
    subtitle: 'Review your request before posting changes to stores.',
    confirmLabel: 'Update request',
    confirmLoadingLabel: 'Updating...',
    // Targeting is frozen on a live request - show the reach snapshot from
    // launch, never recalculate.
    targetingLabel: 'Current reach',
  },
};

// Flattens the form-shape targeting into display chips ({ label, kind }).
export function targetingChipList(targeting = {}) {
  const chips = [];
  const store = (label) => chips.push({ label, kind: 'store' });
  (targeting.store_types || []).forEach((value) => store(value));
  (targeting.states || []).forEach((value) => store(value));
  (targeting.regions || []).forEach((value) => store(`Region ${value}`));
  (targeting.markets || []).forEach((value) => store(`Market ${value}`));
  (targeting.store_numbers || []).forEach((value) => store(`Store ${value}`));
  (targeting.specific_users || []).forEach((value) =>
    chips.push({ label: value, kind: 'user' })
  );
  (targeting.employee_types || []).forEach((value) =>
    chips.push({
      label: value === 'H' ? 'Hourly' : 'Salaried',
      kind: 'user',
    })
  );
  return chips;
}

export const styles = {
  paper: {
    border: '1px solid #dbe2e6',
    borderRadius: '12px',
    boxShadow: '0 20px 50px rgba(4, 31, 65, 0.18)',
    width: '100%',
  },
  header: {
    alignItems: 'flex-start',
    borderBottom: '1px solid #e9ecee',
    display: 'flex',
    gap: 2,
    p: '20px',
    position: 'relative',
  },
  statusIcon: {
    alignItems: 'center',
    backgroundColor: '#e5eefc',
    borderRadius: '50%',
    color: colors.base.walmart_blue,
    display: 'flex',
    flexShrink: 0,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  statusIconGlyph: {
    fontSize: 22,
  },
  title: {
    color: colors.base.bentonville_blue,
    fontSize: '20px',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  subtitle: {
    color: '#656d80',
    fontSize: '14px',
    mt: 0.75,
  },
  closeButton: {
    color: '#656d80',
    position: 'absolute',
    right: 12,
    top: 12,
  },
  content: {
    p: { xs: '20px', sm: '20px 28px 28px' },
  },
  columns: {
    display: 'grid',
    gap: { xs: 3, md: 5 },
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  },
  detailGroup: {
    minWidth: 0,
  },
  label: {
    color: '#656d80',
    fontSize: '14px',
    mb: 0.75,
  },
  value: {
    color: colors.base.bentonville_blue,
    fontSize: '14px',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  titleValue: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 800,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  deadline: {
    alignItems: 'center',
    color: colors.base.bentonville_blue,
    display: 'flex',
    fontSize: '14px',
    gap: 1.25,
  },
  deadlineIcon: {
    fontSize: 16,
  },
  contentItem: {
    alignItems: 'center',
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    display: 'flex',
    gap: 1.5,
    minWidth: 0,
    p: '12px',
  },
  itemNumber: {
    alignItems: 'center',
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '50%',
    color: colors.base.light,
    display: 'flex',
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 800,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  itemText: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 700,
  },
  itemDescription: {
    color: '#656d80',
    display: '-webkit-box',
    fontSize: '14px',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  mediaType: {
    alignItems: 'center',
    backgroundColor: colors.base.light,
    border: '1px solid #e9ecee',
    borderRadius: '8px',
    color: '#656d80',
    display: 'flex',
    flexShrink: 0,
    fontSize: '14px',
    gap: 0.75,
    px: 1.5,
    py: 0.5,
  },
  mediaTypeIcon: {
    fontSize: 14,
  },
  targetingChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mb: 1.5,
  },
  targetingChip: {
    alignItems: 'center',
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '20px',
    color: colors.base.light,
    display: 'flex',
    fontSize: '14px',
    fontWeight: 700,
    gap: 0.5,
    px: 1.75,
    py: 0.75,
  },
  targetingIcon: {
    fontSize: 15,
  },
  targetingSummary: {
    // Same light-gray card treatment as the content item rows above.
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 2,
    p: '14px 16px',
  },
  targetingMetric: {
    minWidth: 0,
  },
  metricValue: {
    color: colors.base.bentonville_blue,
    fontSize: '22px',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  metricTotal: {
    color: '#656d80',
    fontSize: '14px',
    fontWeight: 400,
    ml: 0.5,
  },
  metricLabel: {
    color: '#656d80',
    fontSize: '14px',
    mt: 0.25,
  },
  actions: {
    borderTop: '1px solid #e9ecee',
    gap: 1,
    p: '16px 20px',
  },
  button: {
    ...adminFormStyles.secondaryButton,
    color: colors.base.bentonville_blue,
  },
  confirmButton: adminFormStyles.primaryButton,
};

function MediaType({ type }) {
  const isVideo = type === MEDIA_TYPES.VIDEO;

  return (
    <Box sx={styles.mediaType}>
      {isVideo ? (
        <VideocamOutlined sx={styles.mediaTypeIcon} />
      ) : (
        <PhotoCameraOutlined sx={styles.mediaTypeIcon} />
      )}
      {isVideo ? 'Video' : 'Photo'}
    </Box>
  );
}

MediaType.propTypes = {
  type: PropTypes.oneOf(Object.values(MEDIA_TYPES)).isRequired,
};

export default function TaskLaunchModal({
  isLaunching,
  isOpen,
  isReachLoading,
  mode,
  onClose,
  onConfirm,
  reach,
  task,
}) {
  const copy = COPY[mode];
  const chips = targetingChipList(task.targeting);
  // UPDATE shows the frozen snapshot persisted at launch; LAUNCH shows the
  // reach calculated when the modal opened. (`?? reach` is a defensive fallback
  // for an older live task saved before reach snapshots existed.)
  const reachData =
    mode === TASK_REVIEW_MODES.UPDATE ? task.reach ?? reach : reach;

  function renderMetrics() {
    if (mode === TASK_REVIEW_MODES.LAUNCH && isReachLoading) {
      return (
        <Typography sx={{ ...styles.metricLabel, gridColumn: '1 / -1' }}>
          Calculating reach…
        </Typography>
      );
    }

    if (!reachData) {
      return (
        <>
          <Box sx={styles.targetingMetric}>
            <Typography sx={styles.metricValue}>All</Typography>
            <Typography sx={styles.metricLabel}>stores</Typography>
          </Box>
          <Box sx={styles.targetingMetric}>
            <Typography sx={styles.metricValue}>All</Typography>
            <Typography sx={styles.metricLabel}>associates</Typography>
          </Box>
        </>
      );
    }

    const totalStores = reachData.total_store_count;

    return (
      <>
        <Box sx={styles.targetingMetric}>
          <Typography sx={styles.metricValue}>
            {reachData.store_count.toLocaleString()}
            {totalStores != null && (
              <Box component="span" sx={styles.metricTotal}>
                of {totalStores.toLocaleString()}
              </Box>
            )}
          </Typography>
          <Typography sx={styles.metricLabel}>stores targeted</Typography>
        </Box>
        <Box sx={styles.targetingMetric}>
          <Typography sx={styles.metricValue}>
            {reachData.user_count.toLocaleString()}
          </Typography>
          <Typography sx={styles.metricLabel}>associates targeted</Typography>
        </Box>
      </>
    );
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={isOpen}
      onClose={isLaunching ? undefined : onClose}
      PaperProps={{ sx: styles.paper }}
      aria-labelledby="task-launch-title"
      aria-describedby="task-launch-description"
    >
      <Box sx={styles.header}>
        <Box sx={styles.statusIcon}>
          <CheckCircleOutline sx={styles.statusIconGlyph} />
        </Box>
        <Box>
          <Typography component="h2" id="task-launch-title" sx={styles.title}>
            {copy.title}
          </Typography>
          <Typography id="task-launch-description" sx={styles.subtitle}>
            {copy.subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          disabled={isLaunching}
          onClick={onClose}
          size="small"
          sx={styles.closeButton}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        <Box sx={styles.columns}>
          <Stack spacing={2.5}>
            <Box sx={styles.detailGroup}>
              <Typography sx={styles.label}>Title</Typography>
              <Typography sx={styles.titleValue}>{task.title}</Typography>
            </Box>
            <Box sx={styles.detailGroup}>
              <Typography sx={styles.label}>Deadline</Typography>
              <Box sx={styles.deadline}>
                <CalendarTodayOutlined sx={styles.deadlineIcon} />
                {moment(task.due_date).format('MM/DD/YYYY')}
              </Box>
            </Box>
            <Box sx={styles.detailGroup}>
              <Typography sx={styles.label}>Description</Typography>
              <Typography sx={styles.value}>{task.description}</Typography>
            </Box>
          </Stack>

          <Stack spacing={2.5}>
            <Box>
              <Typography sx={styles.label}>
                Content items ({task.content_requests.length})
              </Typography>
              <Stack spacing={1.5}>
                {task.content_requests.map((contentRequest, index) => (
                  <Box key={contentRequest.ui_id} sx={styles.contentItem}>
                    <Box sx={styles.itemNumber}>{index + 1}</Box>
                    <Box sx={styles.itemText}>
                      <Typography sx={styles.itemTitle}>
                        {contentRequest.title}
                      </Typography>
                      <Typography sx={styles.itemDescription}>
                        {contentRequest.description}
                      </Typography>
                    </Box>
                    <MediaType type={contentRequest.media_type} />
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography sx={styles.label}>{copy.targetingLabel}</Typography>
              {chips.length > 0 ? (
                <Box sx={styles.targetingChips}>
                  {chips.map((chip) => (
                    <Box key={chip.label} sx={styles.targetingChip}>
                      {chip.kind === 'user' ? (
                        <PersonOutlineOutlined sx={styles.targetingIcon} />
                      ) : (
                        <LocationOnOutlined sx={styles.targetingIcon} />
                      )}
                      {chip.label}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={styles.value}>
                  No filters — every store and associate.
                </Typography>
              )}
              <Box sx={styles.targetingSummary}>{renderMetrics()}</Box>
            </Box>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button
          disabled={isLaunching}
          onClick={onClose}
          sx={styles.button}
          variant="outlined"
        >
          Go back
        </Button>
        <Button
          disabled={isLaunching}
          onClick={onConfirm}
          startIcon={
            isLaunching ? <CircularProgress color="inherit" size={16} /> : null
          }
          sx={styles.confirmButton}
          variant="contained"
        >
          {isLaunching ? copy.confirmLoadingLabel : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const reachShape = PropTypes.shape({
  store_count: PropTypes.number,
  user_count: PropTypes.number,
  total_store_count: PropTypes.number,
});

TaskLaunchModal.propTypes = {
  isLaunching: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  isReachLoading: PropTypes.bool,
  mode: PropTypes.oneOf(Object.values(TASK_REVIEW_MODES)),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  reach: reachShape,
  task: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    due_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    targeting: PropTypes.shape({}),
    reach: reachShape,
    content_requests: PropTypes.arrayOf(
      PropTypes.shape({
        ui_id: PropTypes.string.isRequired,
        title: PropTypes.string,
        description: PropTypes.string,
        media_type: PropTypes.oneOf(Object.values(MEDIA_TYPES)),
      })
    ).isRequired,
  }).isRequired,
};

TaskLaunchModal.defaultProps = {
  isLaunching: false,
  isReachLoading: false,
  mode: TASK_REVIEW_MODES.LAUNCH,
  reach: null,
};
